import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem,
  FormControl, InputLabel, TextField, Alert, CircularProgress
} from "@mui/material";
import {
  Cancel, CheckCircle, Refresh, Delete, Warning, Edit, Timer
} from "@mui/icons-material";
import {
  collection, onSnapshot, query, orderBy, limit, doc, updateDoc,
  deleteDoc, Timestamp, where
} from "firebase/firestore";
import { db } from "../firebase";
import { STATUS_COLORS } from "../theme";

export interface JobManagementProps {
  highlightJobId?: string | null;
}

const ACTIVE_STATUSES = ["pending", "matched", "dispatched", "accepted", "en_route", "arrived", "in_progress"];
const ALL_STATUSES = [...ACTIVE_STATUSES, "completed", "couldnt_fix", "needs_revisit", "cancelled"];

export function JobManagementView({ highlightJobId }: JobManagementProps = {}) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [techs, setTechs] = useState<Record<string, string>>({});
  const [filterStatus, setFilterStatus] = useState("active");
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [dialogType, setDialogType] = useState<"cancel" | "complete" | "status" | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const highlightRef = React.useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    const q = filterStatus === "active"
      ? query(collection(db, "jobs"), orderBy("createdAt", "desc"), limit(100))
      : query(collection(db, "jobs"), orderBy("createdAt", "desc"), limit(200));

    return onSnapshot(q, snap => {
      let docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (filterStatus === "active") {
        docs = docs.filter((j: any) => ACTIVE_STATUSES.includes(j.status));
      } else if (filterStatus !== "all") {
        docs = docs.filter((j: any) => j.status === filterStatus);
      }
      setJobs(docs);
    });
  }, [filterStatus]);

  // Load technician names for display
  useEffect(() => {
    return onSnapshot(collection(db, "technicians"), snap => {
      const map: Record<string, string> = {};
      snap.docs.forEach(d => {
        const data = d.data();
        map[d.id] = data.displayName || data.name || d.id.slice(0, 8);
      });
      setTechs(map);
    });
  }, []);

  // Auto-scroll to highlighted job when navigating from dashboard
  useEffect(() => {
    if (highlightJobId && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    }
  }, [highlightJobId, jobs]);

  const fmt = (ts: any) => {
    if (!ts?.toDate) return "—";
    return ts.toDate().toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
    });
  };

  const showAlert = (type: "success" | "error", msg: string) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleCancel = async () => {
    if (!selectedJob) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        status: "cancelled",
        cancelledAt: Timestamp.now(),
        cancelledBy: "admin",
        updatedAt: Timestamp.now(),
      });
      showAlert("success", `Job ${selectedJob.id.slice(0, 8)}… cancelled`);
      setDialogType(null);
    } catch (e: any) {
      showAlert("error", e.message);
    }
    setLoading(false);
  };

  const handleForceComplete = async () => {
    if (!selectedJob) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        status: "completed",
        completedAt: Timestamp.now(),
        completedBy: "admin",
        totalCharge: selectedJob.totalCharge ?? 0,
        updatedAt: Timestamp.now(),
      });
      showAlert("success", `Job marked completed`);
      setDialogType(null);
    } catch (e: any) {
      showAlert("error", e.message);
    }
    setLoading(false);
  };

  const handleStatusChange = async () => {
    if (!selectedJob || !newStatus) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });
      showAlert("success", `Status updated to ${newStatus}`);
      setDialogType(null);
    } catch (e: any) {
      showAlert("error", e.message);
    }
    setLoading(false);
  };

  const handleDelete = async (job: any) => {
    if (!window.confirm(`Delete job ${job.id.slice(0, 8)}…? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "jobs", job.id));
      showAlert("success", "Job deleted");
    } catch (e: any) {
      showAlert("error", e.message);
    }
  };

  const openDialog = (job: any, type: "cancel" | "complete" | "status") => {
    setSelectedJob(job);
    setNewStatus(job.status);
    setDialogType(type);
  };

  return (
    <Box>
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.msg}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filterStatus} label="Filter" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="active">🔴 Active Only</MenuItem>
            <MenuItem value="all">All Jobs</MenuItem>
            {ALL_STATUSES.map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                {s.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {jobs.length} jobs
        </Typography>
        {filterStatus === "active" && jobs.length > 0 && (
          <Chip
            label={`${jobs.length} active jobs need attention`}
            color="warning"
            size="small"
            icon={<Warning />}
          />
        )}
      </Box>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Job ID", "Status", "Category", "Customer", "Tech", "Total", "Created", "Actions"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: "text.secondary", fontSize: 11, textTransform: "uppercase" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map(job => (
                <TableRow
                  key={job.id}
                  hover
                  ref={job.id === highlightJobId ? highlightRef : undefined}
                  sx={{
                    bgcolor: job.id === highlightJobId
                      ? "rgba(139,92,246,0.15)"
                      : ACTIVE_STATUSES.includes(job.status)
                        ? "rgba(255,165,0,0.03)"
                        : "inherit",
                    outline: job.id === highlightJobId ? "1px solid rgba(139,92,246,0.6)" : "none",
                    transition: "background-color 0.4s",
                  }}
                >
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                      {job.id.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Chip
                        label={job.status?.replace(/_/g, " ") || "pending"}
                        size="small"
                        sx={{ bgcolor: STATUS_COLORS[job.status] || "#555", color: "white", fontWeight: 700, fontSize: 10 }}
                      />
                      {job.status === "dispatched" && (() => {
                        const ts = job.dispatchedAt || job["timeline.dispatchedAt"];
                        if (!ts) return null;
                        try {
                          const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
                          const mins = (Date.now() - date.getTime()) / 60000;
                          if (mins < 1) return null;
                          const color = mins >= 5 ? "#ef4444" : mins >= 3 ? "#f59e0b" : "#6b7280";
                          return (
                            <Tooltip title={mins >= 5 ? "Rematch triggered" : mins >= 3 ? "Warning sent" : "Waiting"}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color }}>
                                <Timer sx={{ fontSize: 12 }} />
                                <Typography variant="caption" fontWeight={700} sx={{ color, fontSize: 10 }}>
                                  {Math.floor(mins)}m
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        } catch { return null; }
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                      {job.category?.replace(/_/g, " ") || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                      {job.customerId?.slice(0, 8) || "—"}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(job.technicianId || job.techId) ? (
                      <Typography variant="caption" fontWeight={600}>
                        {/* techName field updated by rematchJob — show it directly if available */}
                        {job.techName ||
                          techs[job.technicianId] ||
                          techs[job.techId] ||
                          (job.technicianId || job.techId)?.slice(0, 8) + "…"}
                      </Typography>
                    ) : (
                      <em style={{ color: "#f59e0b", fontSize: 12 }}>Unassigned</em>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {job.totalCharge != null ? `$${Number(job.totalCharge).toFixed(2)}` : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(job.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Change status">
                        <IconButton size="small" onClick={() => openDialog(job, "status")}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {ACTIVE_STATUSES.includes(job.status) && (
                        <>
                          <Tooltip title="Force complete">
                            <IconButton size="small" color="success" onClick={() => openDialog(job, "complete")}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel job">
                            <IconButton size="small" color="warning" onClick={() => openDialog(job, "cancel")}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete (permanent)">
                        <IconButton size="small" color="error" onClick={() => handleDelete(job)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    {filterStatus === "active" ? "✅ No active jobs — all clear!" : "No jobs found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Cancel Dialog */}
      <Dialog open={dialogType === "cancel"} onClose={() => setDialogType(null)}>
        <DialogTitle>Cancel Job</DialogTitle>
        <DialogContent>
          <Typography>
            Cancel job <strong>{selectedJob?.id?.slice(0, 8)}…</strong>?
            <br /><br />
            This will set status to <strong>cancelled</strong>. If a Stripe PaymentIntent exists,
            you'll need to release it manually in the Stripe Dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Back</Button>
          <Button onClick={handleCancel} color="warning" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Cancel Job"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Complete Dialog */}
      <Dialog open={dialogType === "complete"} onClose={() => setDialogType(null)}>
        <DialogTitle>Force Complete Job</DialogTitle>
        <DialogContent>
          <Typography>
            Mark job <strong>{selectedJob?.id?.slice(0, 8)}…</strong> as completed?
            <br /><br />
            <strong>Note:</strong> This will NOT automatically charge the customer or pay the tech.
            Handle payment manually in Stripe if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Back</Button>
          <Button onClick={handleForceComplete} color="success" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Mark Completed"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={dialogType === "status"} onClose={() => setDialogType(null)} fullWidth maxWidth="xs">
        <DialogTitle>Change Job Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Job: {selectedJob?.id?.slice(0, 8)}… · Current: {selectedJob?.status}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>New Status</InputLabel>
            <Select value={newStatus} label="New Status" onChange={e => setNewStatus(e.target.value)}>
              {ALL_STATUSES.map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: STATUS_COLORS[s] || "#555" }} />
                    {s.replace(/_/g, " ")}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Cancel</Button>
          <Button onClick={handleStatusChange} variant="contained" disabled={loading || newStatus === selectedJob?.status}>
            {loading ? <CircularProgress size={20} /> : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
